import { apiGet } from '../api/client';

/**
 * Stage 12 (A5): module-level gallery cache so switching away and back
 * between Images / Music / Speech / any other gallery page doesn't trigger
 * a fresh network fetch within STALE_MS.
 *
 * Stage 12 (C4): extended with cursor pagination. Each cache entry tracks
 * a `nextCursor` — the `created_at` of the oldest row in the cached page
 * set. `loadMoreGallery` appends the next page to the same cached items
 * array without replacing state.
 */

interface CacheEntry<T extends { id: string } = { id: string }> {
  items: T[];
  loading: boolean;
  error: string | null;
  lastFetchedAt: number;
  inflight: Promise<void> | null;
  subscribers: Set<() => void>;
  /** Stage 12 (C4): cursor for the next page, or null if no more. */
  nextCursor: string | null;
  /** True once any fetch has completed — used to decide between "initial
   *  loading spinner" and "infinite-scroll sentinel". */
  hasFetched: boolean;
}

const cache = new Map<string, CacheEntry>();
export const STALE_MS = 60_000;

function ensure(endpoint: string): CacheEntry {
  let e = cache.get(endpoint);
  if (!e) {
    e = {
      items: [],
      loading: false,
      error: null,
      lastFetchedAt: 0,
      inflight: null,
      subscribers: new Set(),
      nextCursor: null,
      hasFetched: false,
    };
    cache.set(endpoint, e);
  }
  return e;
}

function notify(endpoint: string): void {
  const e = cache.get(endpoint);
  if (!e) return;
  for (const cb of e.subscribers) cb();
}

function appendBefore(endpoint: string, url: string): string {
  // Endpoint keys may already contain a `?type=` query (speech gallery) so
  // we must use the correct separator.
  return url + (url.includes('?') ? '&' : '?');
}

/**
 * Initial-fetch path. Replaces the cached items array. `force: true`
 * bypasses the staleness check.
 */
export async function fetchGallery<T extends { id: string }>(
  endpoint: string,
  force = false,
): Promise<void> {
  const e = ensure(endpoint);
  const now = Date.now();
  if (e.inflight) return e.inflight;
  if (!force && now - e.lastFetchedAt < STALE_MS && e.lastFetchedAt > 0) {
    return;
  }
  e.loading = true;
  e.error = null;
  notify(endpoint);
  const inflight = (async (): Promise<void> => {
    try {
      const res = await apiGet<Record<string, unknown>>(endpoint);
      const firstArray = Object.values(res).find((v) => Array.isArray(v));
      const next =
        typeof res.nextCursor === 'string' && res.nextCursor.length > 0
          ? res.nextCursor
          : null;
      e.items = ((firstArray as T[] | undefined) ?? []) as Array<{
        id: string;
      }>;
      e.nextCursor = next;
      e.lastFetchedAt = Date.now();
      e.hasFetched = true;
    } catch (err) {
      e.error = err instanceof Error ? err.message : String(err);
    } finally {
      e.loading = false;
      e.inflight = null;
      notify(endpoint);
    }
  })();
  e.inflight = inflight;
  return inflight;
}

/**
 * Stage 12 (C4): append-fetch for infinite-scroll. Uses the entry's
 * `nextCursor` as the `before=` query arg. No-op when already at the end
 * or when a fetch is already in flight.
 */
export async function loadMoreGallery<T extends { id: string }>(
  endpoint: string,
): Promise<void> {
  const e = ensure(endpoint);
  if (e.inflight) return e.inflight;
  if (!e.nextCursor) return;
  const cursor = e.nextCursor;
  e.loading = true;
  e.error = null;
  notify(endpoint);
  const url = `${appendBefore(endpoint, endpoint)}before=${encodeURIComponent(cursor)}`;
  const inflight = (async (): Promise<void> => {
    try {
      const res = await apiGet<Record<string, unknown>>(url);
      const firstArray = Object.values(res).find((v) => Array.isArray(v));
      const next =
        typeof res.nextCursor === 'string' && res.nextCursor.length > 0
          ? res.nextCursor
          : null;
      const page = ((firstArray as T[] | undefined) ?? []) as Array<{
        id: string;
      }>;
      // Dedupe by id in case of overlap races.
      const seen = new Set(e.items.map((it) => it.id));
      for (const item of page) if (!seen.has(item.id)) e.items.push(item);
      e.nextCursor = next;
      e.lastFetchedAt = Date.now();
    } catch (err) {
      e.error = err instanceof Error ? err.message : String(err);
    } finally {
      e.loading = false;
      e.inflight = null;
      notify(endpoint);
    }
  })();
  e.inflight = inflight;
  return inflight;
}

export function subscribeGallery(
  endpoint: string,
  cb: () => void,
): () => void {
  const e = ensure(endpoint);
  e.subscribers.add(cb);
  return (): void => {
    e.subscribers.delete(cb);
  };
}

export function getGalleryEntry<T extends { id: string }>(
  endpoint: string,
): CacheEntry<T> | undefined {
  return cache.get(endpoint) as CacheEntry<T> | undefined;
}

/**
 * Optimistic mutate: apply a transform to the cached items array without
 * refetching. Used by optimistic prepend (create-new, Stage A6) and
 * optimistic remove (bulk delete, Stage D1).
 */
export function mutateGallery<T extends { id: string }>(
  endpoint: string,
  fn: (items: T[]) => T[],
): void {
  const e = ensure(endpoint);
  e.items = fn(e.items as T[]) as Array<{ id: string }>;
  notify(endpoint);
}

/** Completely evict a cache entry (e.g. on Clear cache actions). */
export function invalidateGallery(endpoint: string): void {
  cache.delete(endpoint);
}
